<template>
  <div class="is-flex is-flex-wrap-wrap is-justify-content-space-between">
    <div class="is-flex">
      <ButtonView
        class="feedback-button m-r-7"
        :class="likeButtonClasses"
        type="icon-button"
        :disabled="isRatingDisabled"
        @click="onRate(1)"
        :data-e2e="AICypressAttribute.buttons.FEEDBACK_THUMBS_UP"
      >
        <i class="cls is-size-6 has-text-weight-semibold" :class="likeIconClass"></i>
      </ButtonView>
      <ButtonView
        class="feedback-button"
        :class="dislikeButtonClasses"
        type="icon-button"
        :disabled="isRatingDisabled"
        @click="onRate(0)"
        :data-e2e="AICypressAttribute.buttons.FEEDBACK_THUMBS_DOWN"
      >
        <i
          class="cls is-size-6 has-text-weight-semibold is-relative xy-t-2"
          :class="dislikeIconClass"
        ></i>
      </ButtonView>
      <template v-if="showSubmitToLibraryButton">
        <div
          v-if="answer.isInLibrary"
          class="is-size-7 has-text-grey has-text-weight-semibold is-flex is-align-items-center ml-5 mr-3 is-relative xy-b-2"
        >
          <i class="cls cls-check-circle-1-filled m-r-5"></i>
          {{ $t('ai.added-to-library') }}
        </div>
        <TooltipView
          v-else
          type="is-dark"
          class="is-relative xy-b-3 mx-4"
          :multilined="true"
          :label="$t('ai.pin-this-answer')"
        >
          <ButtonView
            class="sources-button button is-ghost is-borderless"
            :data-e2e="AICypressAttribute.buttons.CLARA_ANSWER_ADD_TO_LIBRARY"
            @click="onAddToLibraryClick"
          >
            <div class="is-size-6 has-text-primary">
              <i class="cls cls-send-email m-r-5 is-relative xy-t-2"></i>
              <span class="has-text-weight-semibold">
                {{ $t('ai.submit-to-library') }}
              </span>
            </div>
          </ButtonView>
        </TooltipView>
      </template>
      <ButtonView
        v-if="resolvedSources.length > 0"
        class="sources-button button is-ghost is-borderless is-relative xy-b-3"
        :data-e2e="AICypressAttribute.buttons.CLARA_ANSWER_SOURCES"
        @click="onSourcesClick"
      >
        <div class="is-size-6 has-text-primary">
          <i class="cls cls-common-file-text-alternate m-r-5"></i>
          <span class="has-text-weight-semibold">
            {{ pluralize($t('ai.sources'), resolvedSources.length, true) }}
          </span>
        </div>
      </ButtonView>

      <FloatingDetailPanel
        :show="isSourcesPanelOpen"
        @on-close="onCloseSourcesPanel"
        :overlay="true"
        width="300"
        z-index="150"
      >
        <div class="has-background-white p-3">
          <p class="has-text-weight-semibold px-2 m-b-10">
            {{ $t('ai.sources') }}
            <span class="is-size-6" v-if="resolvedSources.length">
              ({{ resolvedSources.length }})
            </span>
          </p>
          <ClaraSource
            v-for="entry in resolvedSources"
            class="m-b-10"
            :key="entry.source.id"
            :document="entry.document"
            :source="entry.source"
            :has-border="false"
            :has-hover="true"
          />
        </div>
      </FloatingDetailPanel>
    </div>
    <section
      v-if="isFeedbackFormOpen"
      class="is-full-width m-t-15"
      :data-e2e="AICypressAttribute.views.CLARA_FEEDBACK_FORM"
    >
      <p class="has-text-weight-semibold">
        {{ $t('ai.how-we-can') }}
        <span v-if="feedback.isPositive">({{ $t('auth.optional') }})</span>
      </p>
      <p class="is-size-6 has-text-grey m-t-5">
        {{ $t('ai.your-feedback-helps') }}
      </p>
      <FormTextarea
        v-model="feedback.description"
        class="m-t-10"
        type="text"
        rows="3"
        :placeholder="$t('ai.describe-what-was')"
      />
      <div class="is-flex is-justify-content-flex-end m-t-10">
        <ButtonView
          type="is-primary is-light"
          class="m-r-10"
          :disabled="isSavingFeedback"
          :data-e2e="AICypressAttribute.buttons.CLARA_FEEDBACK_CANCEL"
          @click="onFeedbackFormCancel"
        >
          <span class="has-text-weight-semibold">{{ $t('core.cancel') }}</span>
        </ButtonView>
        <ButtonView
          type="is-primary"
          :disabled="isFeedbackSubmitDisabled"
          :loading="isSavingFeedback"
          :data-e2e="AICypressAttribute.buttons.CLARA_FEEDBACK_SUBMIT"
          @click="submitFeedback"
        >
          <span class="has-text-weight-semibold">{{ $t('estate.submit') }}</span>
        </ButtonView>
      </div>
    </section>
    <p
      v-else-if="isFeedbackSubmitted"
      class="is-full-width is-size-6 has-text-grey m-t-10"
      :data-e2e="AICypressAttribute.views.THANKS_FOR_FEEDBACK"
    >
      {{ $t('ai.thank-you-for-feedback') }} {{ $t('ai.our-team-will') }}
    </p>
  </div>
</template>

<script>
import pluralize from 'pluralize';
import { AICypressAttribute } from '@/modules/ai/constants/AICypressAttribute';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import FloatingDetailPanel from '@/modules/core/app/components/FloatingDetailPanel.vue';
import ClaraSource from '@/modules/ai/components/presentational/ClaraSource.vue';
import { ClaraFeedbackMixin } from '@/modules/ai/components/stateful/mixins/ClaraFeedbackMixin';
import { EstateRagFeedbackObject } from '@/modules/ai/models/EstateRagFeedbackObject';
import ClaraService from '@/modules/ai/services/ClaraService';
import ModalService from '@/modules/core/ui/services/ModalService';
import ClaraFeedbackSubmittedModal from '@/modules/ai/components/presentational/ClaraFeedbackSubmittedModal.vue';
import ToastService from '@/modules/core/ui/services/ToastService';
import TooltipView from '@/modules/core/ui/components/wrappers/TooltipView.vue';
import { ClaraConversationOrigin } from '@/modules/ai/constants/ClaraConversationOrigin';
import FormTextarea from '@/modules/core/form/components/FormTextarea.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'ClaraMetadataView',
  mixins: [ClaraFeedbackMixin],
  components: {
    ButtonView,
    FloatingDetailPanel,
    ClaraSource,
    TooltipView,
    FormTextarea
  },
  props: {
    showSubmitToLibraryButton: {
      type: Boolean,
      default: true
    },
    origin: {
      type: String,
      default: ClaraConversationOrigin.CE_APP,
      validator: (value) => Object.values(ClaraConversationOrigin).includes(value)
    }
  },
  static: {
    AICypressAttribute,
    pluralize
  },
  data() {
    return {
      isSourcesPanelOpen: false,
      isFeedbackFormOpen: false,
      isFeedbackSubmitted: false,
      feedback:
        this.answer.feedback ??
        EstateRagFeedbackObject.mapModel({
          modelId: this.answer.id
        })
    };
  },
  computed: {
    resolvedSources() {
      return (this.answer.similarityData ?? [])
        .map((source) => ({ source, document: this.answer.getDocument(source.metadata) }))
        .filter((entry) => entry.document);
    },
    likeButtonClasses() {
      return this.feedback.isPositive ? 'is-primary is-light' : 'is-ghost';
    },
    dislikeButtonClasses() {
      return this.feedback.isNegative ? 'is-primary is-light' : 'is-ghost';
    },
    likeIconClass() {
      return this.feedback.isPositive
        ? 'cls-like-1 has-text-primary'
        : 'cls-like-light has-text-primary';
    },
    dislikeIconClass() {
      return this.feedback.isNegative
        ? 'cls-dislike-1 has-text-primary'
        : 'cls-dislike-light has-text-primary';
    },
    isPlannerConversation() {
      return this.origin === ClaraConversationOrigin.PLANNER;
    },
    isRatingDisabled() {
      return this.isSavingFeedback || this.isFeedbackFormOpen;
    },
    isFeedbackSubmitDisabled() {
      return !this.feedback.isPositive && !this.feedback.description;
    }
  },
  methods: {
    onSourcesClick() {
      this.isSourcesPanelOpen = true;
    },
    onCloseSourcesPanel() {
      this.isSourcesPanelOpen = false;
    },
    showConfirmationModal() {
      if (!this.isPlannerConversation) {
        ClaraFeedbackMixin.methods.showConfirmationModal.call(this);
        return;
      }

      this.isFeedbackSubmitted = false;
      this.isFeedbackFormOpen = true;
    },
    onFeedbackFormCancel() {
      this.isFeedbackFormOpen = false;
      this.feedback.value = this.originalValue;
      this.feedback.description = this.originalDescription;
    },
    async submitFeedback() {
      try {
        this.isSavingFeedback = true;
        this.answerToEdit.feedback = await ClaraService.submitMessageFeedback(this.feedback);
      } finally {
        this.isSavingFeedback = false;
        this.showFeedbackSubmitted();
      }
    },
    showFeedbackSubmitted() {
      if (this.isPlannerConversation) {
        this.isFeedbackFormOpen = false;
        this.isFeedbackSubmitted = true;
        return;
      }

      ModalService.show({
        parent: this,
        component: ClaraFeedbackSubmittedModal,
        fullScreen: false,
        events: {
          'on-close': () => {
            ModalService.close();
          }
        }
      });
    },
    onAddToLibraryClick() {
      ClaraService.addMessageToLibrary(this.answer.identifier, true);
      this.answer.isInLibrary = true;
      ToastService.show(this.$t('ai.added-to-library'));
    }
  }
};
</script>

<style scoped>
:deep(.feedback-button.button) {
  &:hover {
    box-shadow: none !important;
  }
}
:deep(.sources-button.button) {
  &:hover {
    text-decoration: none !important;
  }
}
</style>
